import { useEventListener } from "@vueuse/core";
import { computed, onMounted, ref, toRef, useTemplateRef, watch } from "vue";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useExpose } from "#ce/composables/useExpose";
import { useDistanceSortPrompt } from "./useDistanceSortPrompt";
import { useSortOptions } from "./useSortOptions";
import { useSortSelect } from "./useSortSelect";
import { useFinderSortCommand } from "./useFinderSortCommand";
import { useHostElement } from "#ce/composables/useHostElement";

/** Returns sort options, draft selection and handlers for applying and reporting sort choices. */
export function useSortControl(props) {
  const host = useHostElement();
  const menu = useTemplateRef("menu");

  const { disabled: isDisabled } = useFormControl({ disabled: toRef(props, "disabled") });

  const draftValue = ref("");

  const { sort: finderSort, params: finderParams, report: reportSort } = useFinderSortCommand();

  const boundSource = useSortSelect(() => props.selectId, { exposeValue: false });
  const ownedSource = useSortOptions({ exposeValue: false });
  const source = computed(() => (props.selectId ? boundSource : ownedSource));
  const options = computed(() => source.value.options.value);
  const selectedValue = computed(() => source.value.selectedValue.value);
  const isBound = computed(() => source.value.isBound.value);
  const isMenuDisabled = computed(() => !isBound.value || isDisabled.value);
  const { mirrorFormValue, onFormReset } = useForm();
  mirrorFormValue(selectedValue);
  onFormReset(resetValue);
  const { exposeProperties } = useExpose();
  exposeProperties({
    value: {
      get: () => selectedValue.value,
      set: setValue,
    },
  });
  const { shouldPromptFor, promptForLocation, isLocationResolutionEvent } = useDistanceSortPrompt(
    () => props.nearFilterSelector,
    { params: finderParams, root: () => host?.getRootNode() ?? document },
  );

  function handleLocationResolutionChange(event) {
    if (isLocationResolutionEvent(event, selectedValue.value)) {
      menu.value?.close({ restoreFocus: false });
    }
  }

  function setValue(value) {
    source.value.setValue(value);
  }

  function resetValue() {
    source.value.reset();
  }

  function showFinderSort(sort) {
    if (sort) {
      setValue(sort);
    }
  }

  watch(finderSort, showFinderSort, { immediate: true });

  useEventListener(document, "change", handleLocationResolutionChange);

  const selectedLabel = computed(() => {
    const selectedOption = options.value.find((option) => option.value === selectedValue.value) || options.value[0];

    return selectedOption?.label || "";
  });

  const menuTriggerPrefix = computed(() => (selectedLabel.value ? props.triggerPrefix : ""));

  function syncDraftValue(value) {
    draftValue.value = value;
  }

  watch(selectedValue, syncDraftValue);

  function initializeDraftValue() {
    draftValue.value = selectedValue.value;
    if (shouldPromptFor(selectedValue.value)) {
      requestAnimationFrame(promptForLocation);
    }
  }

  onMounted(initializeDraftValue);

  function handleSelect(value) {
    if (isDisabled.value) {
      return;
    }

    draftValue.value = value;
    applySelection();
  }

  function handleApply() {
    if (isDisabled.value) {
      return;
    }

    // Done button: re-apply idempotently and skip the location prompt so users
    // who confirmed their distance choice without setting a location don't get
    // the near filter forced open.
    source.value.apply(draftValue.value);
  }

  function applySelection() {
    source.value.apply(draftValue.value);
    reportSort(draftValue.value);
    if (shouldPromptFor(draftValue.value)) {
      // Close the sort dialog before opening the near filter — interactions inside
      // the near filter don't bubble to document, so outside-click detection won't
      // auto-close the sort dialog.
      menu.value?.close({ restoreFocus: false });
      promptForLocation();
    }
  }
  return {
    menu,
    draftValue,
    options,
    selectedLabel,
    isMenuDisabled,
    menuTriggerPrefix,
    handleSelect,
    handleApply,
  };
}
